import type { TypedMoney } from "@commercetools/platform-sdk";
import { css } from "@emotion/react";
import { Link } from "react-router";
import { ContentContainer } from "../../components/_shared-components/arrangement/Container";
import { ContentfulImage2 } from "../../components/_shared-components/image-related/ContentfulImage2";
import { ImageCarouselComponentHeadline } from "../../components/_shared-components/ui-elements/SwarovskiGenericCarouselComponent/ImageCarouselComponentHeadline";
import { CSS_MQ } from "../../styles/breakpoints";
import { FONTS, fontSize } from "../../styles/fonts";
import { usePdpTranslations } from "./PdpTranslationsContext";
import { ProductNameWithPrice } from "./ProductNameWithPrice";
import { T } from "../../components/T";

export type PDPBundleContentsElement = {
  headline: string;
  attributes: Array<string>;
  image:
    | {
        url: string;
        sys?: {
          id: string;
        };
      }
    | undefined;
  price: TypedMoney | undefined;
  link: string;
  shortDescription: string | undefined;
};

export const PDPBundleContents: React.FC<{
  elements: Array<PDPBundleContentsElement>;
}> = ({ elements }) => {
  const t = usePdpTranslations();

  return (
    <ContentContainer>
      <ImageCarouselComponentHeadline>
        <T>{t("bundleContents.headline")}</T>
      </ImageCarouselComponentHeadline>

      <ul
        css={css`
          display: flex;
          flex-direction: column;
          gap: 12px;

          ${CSS_MQ.atLeastMd} {
            display: grid;
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 24px;
          }
        `}
      >
        {elements.map(element => (
          <li
            key={JSON.stringify(element)}
            className="u-bg-so-green-white"
            css={css`
              padding: 24px;
              ${CSS_MQ.atLeastMd} {
                padding: 24px;
              }

              display: grid;

              --size: 80px;
              ${CSS_MQ.atLeastMd} {
                --size: 110px;
              }

              grid-template-columns: var(--size) 1fr;
              gap: 12px;
            `}
          >
            <ContentfulImage2
              assetId={element.image?.sys?.id}
              contentfulUrl={element.image?.url}
              alt={element.headline}
              css={css`
                width: 100%;
              `}
              withTransparency={true}
              cropping={undefined}
            />

            <Link
              to={element.link}
              css={css`
                display: flex;
                flex-direction: column;
                gap: 4px;
                margin-top: 12px;

                text-decoration: none;
                color: inherit;
                &:hover {
                  ._headline {
                    text-decoration: underline;
                  }
                }
              `}
            >
              <ProductNameWithPrice
                productName={element.headline}
                price={element.price}
                attributes={element.attributes}
              />
            </Link>

            <p
              css={[
                fontSize.x25,
                FONTS.SwarovskiFutura300,
                css`
                  margin-top: 0;
                  margin-bottom: 0;

                  grid-column: span 2;

                  ${CSS_MQ.atLeastLg} {
                    grid-column: 2 / span 1;
                  }
                `,
              ]}
            >
              {element.shortDescription}
            </p>
          </li>
        ))}
      </ul>
    </ContentContainer>
  );
};
